
interface ComboItineraryProps {
  lang: 'en' | 'ja';
}

export default function ComboItinerary({ lang }: ComboItineraryProps) {
  const t = {
    en: {
      badge: 'Sample Itinerary',
      title: 'How Your Night Unfolds',
      items: [
        {
          time: '7:00 PM',
          icon: 'ri-map-pin-2-line',
          color: 'amber',
          title: 'Meet Up',
          desc: 'We meet at a convenient spot near your Airbnb or a local station. Quick intro and off we go!',
        },
        {
          time: '7:15 PM',
          icon: 'ri-beer-line',
          color: 'amber',
          title: 'Izakaya Time',
          desc: 'Head to a hand-picked neighborhood izakaya. Order drinks, share small plates, and soak in the local atmosphere. No tourist traps — just the real deal.',
        },
        {
          time: '9:15 PM',
          icon: 'ri-walk-line',
          color: 'white',
          title: 'Walk to Karaoke',
          desc: 'A short stroll to the karaoke venue. We handle the reservation and check-in — you just enjoy.',
        },
        {
          time: '9:30 PM',
          icon: 'ri-mic-fill',
          color: 'pink',
          title: 'Karaoke Box',
          desc: 'Private room, big screen, tambourines ready. Sing J-Pop, anime classics, or English hits. We\'ll hype you up and help with song selection.',
        },
        {
          time: '11:30 PM',
          icon: 'ri-star-smile-line',
          color: 'rose',
          title: 'End of Night',
          desc: 'Wrap up with big smiles and unforgettable memories. We\'ll help you get back safely.',
        },
      ],
      note: '* Times are approximate and flexible based on your pace.',
    },
    ja: {
      badge: 'サンプルスケジュール',
      title: '夜の流れ',
      items: [
        {
          time: '19:00',
          icon: 'ri-map-pin-2-line',
          color: 'amber',
          title: '集合',
          desc: 'Airbnb近くの駅や便利な場所で待ち合わせ。簡単な自己紹介をしてスタート！',
        },
        {
          time: '19:15',
          icon: 'ri-beer-line',
          color: 'amber',
          title: '居酒屋タイム',
          desc: '厳選した地元の居酒屋へ。ドリンクを注文して、おつまみをシェアしながら地元の雰囲気を満喫。観光客向けではなく、本物の居酒屋体験。',
        },
        {
          time: '21:15',
          icon: 'ri-walk-line',
          color: 'white',
          title: 'カラオケへ移動',
          desc: 'カラオケ会場まで徒歩移動。予約・受付は全てこちらで対応。あなたは楽しむだけ。',
        },
        {
          time: '21:30',
          icon: 'ri-mic-fill',
          color: 'pink',
          title: 'カラオケボックス',
          desc: 'プライベートルームで大画面・タンバリン完備。J-Pop・アニメ・英語曲、何でもOK。曲選びもサポートします。',
        },
        {
          time: '23:30',
          icon: 'ri-star-smile-line',
          color: 'rose',
          title: '終了',
          desc: '最高の笑顔と思い出で締めくくり。帰り道も安心してご案内します。',
        },
      ],
      note: '※ 時間はあくまで目安です。ペースに合わせて柔軟に対応します。',
    },
  }[lang];

  const colorMap: Record<string, { dot: string; icon: string; line: string }> = {
    amber: { dot: 'bg-amber-500 border-amber-400', icon: 'text-amber-400', line: 'border-amber-500/30' },
    pink: { dot: 'bg-pink-500 border-pink-400', icon: 'text-pink-400', line: 'border-pink-500/30' },
    rose: { dot: 'bg-rose-500 border-rose-400', icon: 'text-rose-400', line: 'border-rose-500/30' },
    white: { dot: 'bg-white/40 border-white/30', icon: 'text-white/60', line: 'border-white/10' },
  };

  return (
    <section className="py-20 px-4 bg-gray-900 text-white">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-white/5 border border-white/10 rounded-full mb-5">
            <i className="ri-time-line text-amber-400 text-sm"></i>
            <span className="text-gray-300 text-sm">{t.badge}</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white">{t.title}</h2>
        </div>

        {/* Timeline */}
        <div className="relative">
          {/* Vertical line */}
          <div className="absolute left-6 top-0 bottom-0 w-px bg-white/10 hidden sm:block"></div>

          <div className="space-y-8">
            {t.items.map((item, i) => {
              const c = colorMap[item.color];
              return (
                <div key={i} className="flex gap-5 sm:gap-8 items-start group">
                  {/* Dot */}
                  <div className="flex-shrink-0 flex flex-col items-center">
                    <div className={`w-12 h-12 flex items-center justify-center rounded-full border-2 ${c.dot} z-10 relative shadow-lg`}>
                      <i className={`${item.icon} text-lg ${c.icon}`}></i>
                    </div>
                  </div>
                  {/* Content */}
                  <div className="flex-1 bg-white/5 border border-white/10 rounded-xl p-5 group-hover:bg-white/8 transition-all duration-300">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-xs font-bold text-white/40 tracking-widest">{item.time}</span>
                    </div>
                    <h3 className="text-lg font-bold text-white mb-1">{item.title}</h3>
                    <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <p className="text-center text-gray-500 text-xs mt-10">{t.note}</p>
      </div>
    </section>
  );
}
